/**
 * Extended scorecard detail sections for optimization results.
 *
 * Renders per-item breakdown, failure modes, mutations, and evaluation
 * details — matching the granularity of the backend MetaTuner scorecard.
 */

import chalk from 'chalk';

// =============================================================================
// Types
// =============================================================================

export interface DatasetItemScore {
  id: string;
  beforeScore?: number;
  afterScore?: number;
}

export interface FailureModeEntry {
  category: string;
  failures: { description?: string; summary?: string }[];
}

export interface MutationEntry {
  label: string;
  status: 'applied' | 'rejected' | 'pending' | 'skipped';
  priority?: string;
  rationale?: string;
}

export interface EvaluationItemDetail {
  itemId: string;
  score: number;
  success: boolean;
  metrics?: {
    name: string;
    score: number;
    success: boolean;
    failureMode?: string;
    reasoning?: string;
    criteria?: { name: string; score: number; success: boolean }[];
  }[];
}

export interface ScorecardDetailData {
  datasetResults?: DatasetItemScore[];
  failureModes?: FailureModeEntry[];
  mutations?: MutationEntry[];
  evaluationDetails?: EvaluationItemDetail[];
}

// =============================================================================
// Chalk-colored terminal rendering
// =============================================================================

export function renderScorecardDetails(data: ScorecardDetailData): void {
  if (data.datasetResults && data.datasetResults.length > 0) {
    console.log('');
    console.log(chalk.dim('  Per-Item Before vs After'));
    console.log(chalk.dim('  ' + '\u2500'.repeat(70)));
    console.log('  ' + 'Item ID'.padEnd(28) + 'Before'.padEnd(10) + 'After'.padEnd(10) + 'Diff'.padEnd(10) + 'Status');
    console.log(chalk.dim('  ' + '\u2500'.repeat(70)));
    for (const item of data.datasetResults) {
      const id = item.id.substring(0, 26).padEnd(26);
      const before = (item.beforeScore ?? 0).toFixed(4).padEnd(8);
      const after = (item.afterScore ?? 0).toFixed(4).padEnd(8);
      const d = (item.afterScore ?? 0) - (item.beforeScore ?? 0);
      const diffStr = ((d >= 0 ? '+' : '') + d.toFixed(4)).padEnd(8);
      const status = d > 0.01 ? chalk.green('\u2713 \u2191') : d < -0.01 ? chalk.red('\u2717 \u2193') : chalk.dim('\u2500');
      console.log(`  ${id}  ${before}  ${after}  ${diffStr}  ${status}`);
    }
    console.log(chalk.dim('  ' + '\u2500'.repeat(70)));
  }

  if (data.failureModes && data.failureModes.length > 0) {
    console.log('');
    console.log(chalk.dim('  Failure Modes by Category'));
    console.log(chalk.dim('  ' + '\u2500'.repeat(70)));
    for (const fm of data.failureModes) {
      const color: 'red' | 'yellow' | 'green' = fm.failures.length > 3 ? 'red' : fm.failures.length > 0 ? 'yellow' : 'green';
      console.log(chalk[color](`  \u25B8 ${fm.category}`) + chalk.dim(` (${String(fm.failures.length)} failures)`));
      for (const f of fm.failures.slice(0, 5)) {
        console.log(chalk.dim(`    \u2514\u2500 ${(f.description ?? f.summary ?? 'No description').substring(0, 65)}`));
      }
      if (fm.failures.length > 5) {
        console.log(chalk.dim(`    \u2514\u2500 ... and ${String(fm.failures.length - 5)} more`));
      }
    }
  }

  if (data.mutations && data.mutations.length > 0) {
    const applied = data.mutations.filter(m => m.status === 'applied');
    const rejected = data.mutations.filter(m => m.status === 'rejected');
    const pending = data.mutations.filter(m => m.status === 'pending');
    const skipped = data.mutations.filter(m => m.status === 'skipped');
    console.log('');
    console.log(chalk.dim('  Mutations'));
    console.log(chalk.dim('  ' + '\u2500'.repeat(70)));
    console.log(`  Total: ${String(data.mutations.length)}  Applied: ${chalk.green(String(applied.length))}  Rejected: ${chalk.red(String(rejected.length))}  Pending: ${chalk.yellow(String(pending.length))}  Skipped: ${chalk.dim(String(skipped.length))}`);
    if (applied.length > 0) {
      console.log(chalk.green('  Applied:'));
      for (const m of applied) {
        const pri = m.priority === 'critical' || m.priority === 'high'
          ? chalk.red(`[${m.priority.toUpperCase()}]`)
          : chalk.dim(`[${(m.priority ?? 'UNKNOWN').toUpperCase()}]`);
        console.log(`    ${pri} ${m.label}`);
        if (m.rationale) console.log(chalk.dim(`         \u2514\u2500 ${m.rationale.substring(0, 60)}`));
      }
    }
    if (rejected.length > 0) {
      console.log(chalk.red('  Rejected:'));
      for (const m of rejected.slice(0, 5)) {
        console.log(chalk.dim(`    \u2717 ${m.label}`));
      }
      if (rejected.length > 5) console.log(chalk.dim(`    ... and ${String(rejected.length - 5)} more`));
    }
  }

  if (data.evaluationDetails && data.evaluationDetails.length > 0) {
    console.log('');
    console.log(chalk.dim('  Detailed Evaluation Breakdown'));
    console.log(chalk.dim('  ' + '\u2500'.repeat(70)));
    for (const item of data.evaluationDetails) {
      const statusIcon = item.success ? chalk.green('\u2713') : chalk.red('\u2717');
      const metricCount = item.metrics?.length ?? 0;
      console.log(`  ${statusIcon} ${item.itemId}  Score: ${item.score.toFixed(4)}  Metrics: ${String(metricCount)}`);
      if (item.metrics) {
        for (const m of item.metrics) {
          const mIcon = m.success ? chalk.green('\u2713') : chalk.red('\u2717');
          const mode = m.failureMode ? chalk.dim(` [${m.failureMode}]`) : '';
          console.log(`      ${mIcon} ${m.name.padEnd(25)} ${m.score.toFixed(3)}${mode}`);
          if (!m.success && m.reasoning) {
            console.log(chalk.dim(`          \u2514\u2500 ${(m.reasoning.split('\n')[0] ?? '').substring(0, 60)}`));
          }
          if (m.criteria) {
            for (const c of m.criteria) {
              const cIcon = c.success ? chalk.green('\u2713') : chalk.red('\u2717');
              console.log(chalk.dim(`          ${cIcon} ${c.name.substring(0, 20).padEnd(20)} ${c.score.toFixed(3)}`));
            }
          }
        }
      }
    }
  }
}

// =============================================================================
// Plain text rendering (for JSON directive renderedCard)
// =============================================================================

export function buildScorecardDetailsText(data: ScorecardDetailData): string {
  const lines: string[] = [];

  if (data.datasetResults && data.datasetResults.length > 0) {
    lines.push('', 'Per-Item Before vs After', '\u2500'.repeat(70));
    lines.push('  ' + 'Item ID'.padEnd(28) + 'Before'.padEnd(10) + 'After'.padEnd(10) + 'Diff'.padEnd(10) + 'Status');
    lines.push('  ' + '\u2500'.repeat(68));
    for (const item of data.datasetResults) {
      const id = item.id.substring(0, 26).padEnd(26);
      const before = (item.beforeScore ?? 0).toFixed(4).padEnd(8);
      const after = (item.afterScore ?? 0).toFixed(4).padEnd(8);
      const d = (item.afterScore ?? 0) - (item.beforeScore ?? 0);
      const diffStr = ((d >= 0 ? '+' : '') + d.toFixed(4)).padEnd(8);
      const status = d > 0.01 ? '\u2191' : d < -0.01 ? '\u2193' : '\u2500';
      lines.push(`  ${id}  ${before}  ${after}  ${diffStr}  ${status}`);
    }
  }

  if (data.failureModes && data.failureModes.length > 0) {
    lines.push('', 'Failure Modes by Category', '\u2500'.repeat(70));
    for (const fm of data.failureModes) {
      lines.push(`  ${fm.category} (${String(fm.failures.length)} failures)`);
      for (const f of fm.failures.slice(0, 5)) {
        lines.push(`    \u2514\u2500 ${(f.description ?? f.summary ?? 'No description').substring(0, 65)}`);
      }
      if (fm.failures.length > 5) lines.push(`    \u2514\u2500 ... and ${String(fm.failures.length - 5)} more`);
    }
  }

  if (data.mutations && data.mutations.length > 0) {
    const applied = data.mutations.filter(m => m.status === 'applied');
    const rejected = data.mutations.filter(m => m.status === 'rejected');
    const pending = data.mutations.filter(m => m.status === 'pending');
    const skipped = data.mutations.filter(m => m.status === 'skipped');
    lines.push('', 'Mutations', '\u2500'.repeat(70));
    lines.push(`  Total: ${String(data.mutations.length)}  Applied: ${String(applied.length)}  Rejected: ${String(rejected.length)}  Pending: ${String(pending.length)}  Skipped: ${String(skipped.length)}`);
    for (const m of applied) {
      lines.push(`    [${(m.priority ?? 'UNKNOWN').toUpperCase()}] ${m.label}`);
      if (m.rationale) lines.push(`         \u2514\u2500 ${m.rationale.substring(0, 60)}`);
    }
    for (const m of rejected.slice(0, 5)) {
      lines.push(`    \u2717 ${m.label}`);
    }
    if (rejected.length > 5) lines.push(`    ... and ${String(rejected.length - 5)} more`);
  }

  if (data.evaluationDetails && data.evaluationDetails.length > 0) {
    lines.push('', 'Detailed Evaluation Breakdown', '\u2500'.repeat(70));
    for (const item of data.evaluationDetails) {
      const icon = item.success ? '\u2713' : '\u2717';
      lines.push(`  ${icon} ${item.itemId}  Score: ${item.score.toFixed(4)}  Metrics: ${String(item.metrics?.length ?? 0)}`);
      if (item.metrics) {
        for (const m of item.metrics) {
          const mIcon = m.success ? '\u2713' : '\u2717';
          const mode = m.failureMode ? ` [${m.failureMode}]` : '';
          lines.push(`      ${mIcon} ${m.name.padEnd(25)} ${m.score.toFixed(3)}${mode}`);
          if (!m.success && m.reasoning) {
            lines.push(`          \u2514\u2500 ${(m.reasoning.split('\n')[0] ?? '').substring(0, 60)}`);
          }
          if (m.criteria) {
            for (const c of m.criteria) {
              const cIcon = c.success ? '\u2713' : '\u2717';
              lines.push(`          ${cIcon} ${c.name.substring(0, 20).padEnd(20)} ${c.score.toFixed(3)}`);
            }
          }
        }
      }
    }
  }

  return lines.length > 0 ? '\n' + lines.join('\n') : '';
}
